/**
 * IncidentRoutingCard — per-event-type routing matrix for the incident dispatcher.
 * Each row = one event_type; columns = configured providers (PagerDuty, Opsgenie).
 * Empty row = fan to all configured (safe default). Hidden inside PagerDutyCard.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_META = {
  kt_signal:    { label: 'KT signal',          hint: 'Resignation/leave keywords fire (HR)' },
  bounce_spike: { label: 'Bounce spike',       hint: '≥5 email bounces/hour (platform)' },
  sso_deletion: { label: 'SSO deletion',       hint: 'WorkOS connection deleted (security)' },
  test:         { label: 'Test page',          hint: 'Manual test fires from this card' },
};

const PROVIDER_LABEL = { pagerduty: 'PagerDuty', opsgenie: 'Opsgenie' };

const IncidentRoutingCard = ({ pagerDutyConfigured, opsgenieConfigured }) => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rules, setRules] = useState([]);
  const [providers, setProviders] = useState(['pagerduty', 'opsgenie']);
  const [saving, setSaving] = useState(null);

  const fetchRules = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/pagerduty/routing`, { headers });
      setRules(r.data.rules);
      setProviders(r.data.providers);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load routing rules');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchRules(); }, [fetchRules]);

  const toggle = async (event_type, provider, currentlyOn) => {
    setSaving(`${event_type}:${provider}`);
    const current = rules.find(r => r.event_type === event_type) || { providers: [] };
    const next = currentlyOn
      ? current.providers.filter(p => p !== provider)
      : [...current.providers, provider];
    try {
      await axios.put(`${API}/admin/pagerduty/routing`,
        { event_type, providers: next }, { headers });
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const providerConfigured = (p) =>
    (p === 'pagerduty' && pagerDutyConfigured) || (p === 'opsgenie' && opsgenieConfigured);

  return (
    <div className="mt-3" data-testid="routing-card">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs font-semibold">Routing rules</h4>
        <span className="text-[10px] text-[#9CA3AF]">Empty row = fan to every configured provider</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="text-[10px] text-[#9CA3AF] uppercase tracking-wide border-b border-[#E5E7EB]">
              <th className="text-left py-1.5 pr-2">Event type</th>
              {providers.map(p => (
                <th key={p} className="text-center px-3">{PROVIDER_LABEL[p] || p}</th>
              ))}
              <th className="text-left pl-3">Effective</th>
            </tr>
          </thead>
          <tbody>
            {rules.map(rule => {
              const meta = EVENT_META[rule.event_type] || { label: rule.event_type, hint: '' };
              const effective = rule.providers.length > 0
                ? rule.providers.filter(p => providerConfigured(p))
                : providers.filter(p => providerConfigured(p));
              return (
                <tr key={rule.event_type} className="border-b border-[#F3F4F6]" data-testid={`routing-row-${rule.event_type}`}>
                  <td className="py-2 pr-2">
                    <div>
                      <div className="font-semibold text-[11px]">{meta.label}</div>
                      <div className="text-[10px] text-[#9CA3AF]">{meta.hint}</div>
                    </div>
                  </td>
                  {providers.map(p => {
                    const on = rule.providers.includes(p);
                    const busy = saving === `${rule.event_type}:${p}`;
                    return (
                      <td key={p} className="text-center px-3 py-2">
                        <div className="flex flex-col items-center gap-0.5">
                          {busy
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin text-[#9CA3AF]" />
                            : <Switch
                                checked={on}
                                disabled={!providerConfigured(p)}
                                onCheckedChange={() => toggle(rule.event_type, p, on)}
                                data-testid={`routing-toggle-${rule.event_type}-${p}`}
                              />
                          }
                          {!providerConfigured(p) && <span className="text-[9px] text-[#9CA3AF]">not set up</span>}
                        </div>
                      </td>
                    );
                  })}
                  <td className="pl-3 py-2">
                    {effective.length === 0 ? (
                      <Badge variant="secondary" className="text-[9px]">no provider</Badge>
                    ) : (
                      <div className="flex gap-1 flex-wrap">
                        {effective.map(p => (
                          <Badge key={p} className="text-[9px] bg-[#34C759]/10 text-[#34C759]">{PROVIDER_LABEL[p] || p}</Badge>
                        ))}
                        {rule.providers.length === 0 && <span className="text-[9px] text-[#9CA3AF] self-center">(default)</span>}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IncidentRoutingCard;
